import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  Popper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { type HotelOption, type SelectedHotel, useHotelSelector } from './useHotelSelector.js';

const isSelectedHotel = (h: SelectedHotel | HotelOption): h is SelectedHotel =>
  typeof (h as SelectedHotel).hotel === 'string';

/**
 * Mobile hotel multi-select. Renders a button that toggles a Popper with a
 * search field + scrollable options list, mirroring the desktop selector
 * behavior in a touch-friendly form factor.
 */
export function HotelSelectorMobile() {
  const {
    selectedHotels,
    hotelOptions,
    inputValue,
    setInputValue,
    handleChange,
    handleInputChange,
    handleLoadMore,
    shouldLoadMore,
    isLoading,
  } = useHotelSelector();

  const [open, setOpen] = useState(false);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef(0);

  // Restore scroll position after fresh data arrives so load-more doesn't jump.
  useEffect(() => {
    if (listboxRef.current && scrollPositionRef.current > 0) {
      requestAnimationFrame(() => {
        if (listboxRef.current) listboxRef.current.scrollTop = scrollPositionRef.current;
      });
    }
  }, [hotelOptions]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setInputValue('');
  }, [setInputValue]);

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      handleInputChange(e.target.value);
    },
    [handleInputChange, setInputValue]
  );

  const selectedValueIds = useMemo(
    () => new Set((selectedHotels ?? []).map((h) => h.hotel)),
    [selectedHotels]
  );

  const toggleHotelOption = useCallback(
    (option: HotelOption) => {
      if (listboxRef.current) scrollPositionRef.current = listboxRef.current.scrollTop;

      const isSelected = selectedValueIds.has(option.value);
      const nextSelected: (SelectedHotel | HotelOption)[] = isSelected
        ? (selectedHotels ?? []).filter((h) => h.hotel !== option.value)
        : [...(selectedHotels ?? []), option];

      const normalized: HotelOption[] = nextSelected.map((h) =>
        isSelectedHotel(h)
          ? {
              value: h.hotel ?? '',
              label: h.hotelName ?? '',
              slug: h.hotelSlug,
              hotelLogo: h.hotelLogo,
            }
          : h
      );
      handleChange(normalized);
    },
    [selectedHotels, selectedValueIds, handleChange]
  );

  const handleListboxScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const listbox = e.currentTarget;
      if (!listbox) return;
      scrollPositionRef.current = listbox.scrollTop;
      const atBottom = listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 8;
      if (atBottom && shouldLoadMore && !isLoading) handleLoadMore();
    },
    [shouldLoadMore, handleLoadMore, isLoading]
  );

  const selectedCount = selectedHotels?.length ?? 0;

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Stack alignItems="center" direction="row" sx={{ position: 'relative' }}>
        <Button
          ref={toggleButtonRef}
          size="small"
          sx={{
            color: 'text.secondary',
            borderColor: 'divider',
            whiteSpace: 'nowrap',
            fontSize: '0.7rem',
            px: 0.75,
            minWidth: 84,
            flexShrink: 0,
          }}
          variant="outlined"
          onClick={() => setOpen((s) => !s)}
        >
          {selectedCount > 0 ? `Hotels (${selectedCount})` : 'Hotels'}
        </Button>

        <Popper
          anchorEl={toggleButtonRef.current}
          modifiers={[{ name: 'flip', enabled: false }]}
          open={open}
          placement="bottom-start"
          style={{ zIndex: 1300, width: 300 }}
        >
          <Card>
            <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
              <TextField
                autoFocus
                fullWidth
                placeholder="Search hotels..."
                size="small"
                value={inputValue}
                onChange={handleSearchInputChange}
              />
              {selectedHotels && selectedHotels.length > 0 && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ mt: 1, flexWrap: 'wrap', alignItems: 'center' }}
                >
                  {selectedHotels.slice(0, 2).map((hotel) => (
                    <Chip
                      key={hotel.hotel}
                      label={hotel.hotelName}
                      size="small"
                      onDelete={() => {
                        const remaining = selectedHotels.filter((h) => h.hotel !== hotel.hotel);
                        handleChange(
                          remaining.map((h) => ({
                            value: h.hotel ?? '',
                            label: h.hotelName ?? '',
                            slug: h.hotelSlug,
                            hotelLogo: h.hotelLogo,
                          }))
                        );
                      }}
                    />
                  ))}
                  {selectedHotels.length > 2 && (
                    <Chip label={`+${selectedHotels.length - 2} more`} size="small" />
                  )}
                  <IconButton
                    size="small"
                    sx={{ ml: 'auto', p: '4px' }}
                    title="Clear all"
                    onClick={() => handleChange([])}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Box>

            <Box
              ref={listboxRef}
              sx={{ maxHeight: 200, overflowY: 'auto' }}
              onScroll={handleListboxScroll}
            >
              {hotelOptions.length === 0 ? (
                <Box
                  sx={{
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  {isLoading ? <CircularProgress size={20} /> : 'No hotels found'}
                </Box>
              ) : (
                hotelOptions.map((option) => {
                  const isSelected = selectedValueIds.has(option.value);
                  return (
                    <Box
                      key={option.value}
                      sx={{
                        px: 2,
                        py: 1,
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      onClick={() => toggleHotelOption(option)}
                    >
                      <Typography noWrap variant="body2">
                        {option.label}
                      </Typography>
                    </Box>
                  );
                })
              )}
              {isLoading && hotelOptions.length > 0 && (
                <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={16} />
                </Box>
              )}
            </Box>
          </Card>
        </Popper>
      </Stack>
    </ClickAwayListener>
  );
}
